import { useEffect, useRef, useState } from "react";
import { LuPlus } from "react-icons/lu";
import { CiSearch } from "react-icons/ci";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import "./clients.css";
import { Typography } from "@mui/material";
import Loader from "../Components/Loader";
import PaginationFooter from "../Components/PaginationFooter.js";

function Departments({ isSidebarExpanded }) {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [departmentName, setDepartmentName] = useState("");
  const [departmentCode, setDepartmentCode] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isSearching, setIsSearching] = useState(false);
  const hasInitializedItemsPerPage = useRef(false);
  const hasInitializedSearch = useRef(false);

  const fetchDepartments = async (page = 1, perPage = itemsPerPage, search = "") => {
    if(!search.trim()) {
      setLoading(true);
      setIsSearching(false);
    } else {
      setLoading(false);
      setIsSearching(true);
    }
    

    try {
      const params = {
        page,
        per_page: perPage,
        ...(search && { search }),
      };

      const response = await axiosInstance.get(`${API_URL}/api/departments`, {
        params,
      });

      // Handle both possible response structures
      setDepartments(response.data?.data || response.data?.departments || []);

      // Extract pagination info
      const paginationData = response.data?.pagination || {};
      setTotalRecords(paginationData.total || response.data?.total || 0);
    } catch (error) {
      console.error(
        "Failed to fetch departments:",
        error.response?.data || error.message
      );
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchDepartments(1, itemsPerPage, "");
  }, []);

  // Fetch when page changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchDepartments(currentPage, itemsPerPage, searchTerm);
    }
  }, [currentPage]);

  // Fetch when items per page changes
  useEffect(() => {
    if (!hasInitializedItemsPerPage.current) {
      hasInitializedItemsPerPage.current = true;
      return;
    }
    setCurrentPage(1);
    fetchDepartments(1, itemsPerPage, searchTerm);
  }, [itemsPerPage]);

  // Handle search with debounce
  useEffect(() => {
    if (!hasInitializedSearch.current) {
      hasInitializedSearch.current = true;
      return;
    }
    const delayTimer = setTimeout(() => {
      setCurrentPage(1);
      setIsSearching(searchTerm.trim().length > 0);
      fetchDepartments(1, itemsPerPage, searchTerm);
    }, 500);

    return () => clearTimeout(delayTimer);
  }, [searchTerm]);

  const handleOpenCreateModal = () => {
    setCreateError("");
    setDepartmentName("");
    setDepartmentCode("");
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    if (createLoading) {
      return;
    }
    setShowCreateModal(false);
    setCreateError("");
  };

  const handleCreateDepartment = async () => {
    const trimmedName = departmentName.trim();
    const trimmedCode = departmentCode.trim();

    if (!trimmedName || !trimmedCode) {
      setCreateError("Designation Name and Code are required.");
      return;
    }

    setCreateLoading(true);
    setCreateError("");

    try {
      await axiosInstance.post(`${API_URL}/api/department`, {
        name: trimmedName,
        code: trimmedCode,
      });
      setShowCreateModal(false);
      setDepartmentName("");
      setDepartmentCode("");
      setCurrentPage(1);
      fetchDepartments(1, itemsPerPage, "");
    } catch (error) {
      console.error(
        "Failed to create department:",
        error.response?.data || error.message
      );
      setCreateError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create designation."
      );
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div>
      {(loading && !isSearching) && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backdropFilter: "blur(6px)",
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              transform: "scale(2)",
              gap: "10px",
            }}
          >
            <Loader />
          </div>
        </div>
      )}

      <div
        className={`table-container clients-page ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="clients-page-content">
          <div className="clients-page-header">
            <div className="clients-header-titleblock">
              <h2 className="page-title mb-0">Designations</h2>
            </div>
            <div className="clients-header-buttons">
              <div className="clients-header-controls-row">
                <div className="clients-search-inline">
                  <CiSearch size={20} className="clients-search-icon" />
                  <input
                    type="text"
                    value={searchTerm}
                    placeholder="Search Designations..."
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <span
                      className="clients-search-clear"
                      onClick={() => setSearchTerm("")}
                    >
                      &times;
                    </span>
                  )}
                </div>
                <button className="clients-btn-create" onClick={handleOpenCreateModal}>
                  <LuPlus /> <span>New</span>
                </button>
              </div>
            </div>
          </div>

          <div className="clients-table-card">
            <div className="clients-table-wrapper">
              <table className="clients-data-table">
                <colgroup>
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "58%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Sr. No</th>
                    <th>Code</th>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, rowIdx) => (
                      <tr key={`dept-skeleton-${rowIdx}`}>
                        {Array.from({ length: 3 }).map((__, colIdx) => (
                          <td key={colIdx}>
                            <div
                              style={{
                                height: "10px",
                                background: "#e5e8ee",
                                borderRadius: "3px",
                                animation: "pulse 2s ease-in-out infinite",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : departments.length > 0 ? (
                    departments.map((department, index) => (
                      <tr key={department.id || index}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>
                          <span className="clients-name">
                            {department.code || "N/A"}
                          </span>
                        </td>
                        <td>{department.name || "N/A"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="clients-no-data">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationFooter
              totalRecords={totalRecords}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
            />
          </div>
        </div>

        {showCreateModal && (
          <div className="clients-modal-overlay" onClick={handleCloseCreateModal}>
            <div
              className="clients-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="clients-modal-header">
                <h2>Create Designation</h2>
                <button
                  type="button"
                  className="clients-btn-close"
                  onClick={handleCloseCreateModal}
                  disabled={createLoading}
                >
                  &times;
                </button>
              </div>

              <form>
                <div className="clients-form-group">
                  <label htmlFor="department-name">Designation Name *</label>
                  <input
                    id="department-name"
                    type="text"
                    placeholder="Enter designation name"
                    value={departmentName}
                    onChange={(e) => setDepartmentName(e.target.value)}
                    disabled={createLoading}
                  />
                </div>

                <div className="clients-form-group">
                  <label htmlFor="department-code">Code *</label>
                  <input
                    id="department-code"
                    type="text"
                    placeholder="Enter designation code"
                    value={departmentCode}
                    onChange={(e) => setDepartmentCode(e.target.value)}
                    disabled={createLoading}
                  />
                </div>

                {createError && (
                  <p className="error-text" style={{ textAlign: "left", marginBottom: "12px" }}>
                    {createError}
                  </p>
                )}

                <div className="clients-modal-footer">
                  <button
                    type="button"
                    className="clients-btn-cancel"
                    onClick={handleCloseCreateModal}
                    disabled={createLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="clients-btn-submit"
                    onClick={handleCreateDepartment}
                    disabled={createLoading}
                  >
                    {createLoading ? "Creating..." : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Departments;
